/**
 * Messaging rail tabs — Messages · Flows · Delivery · Booking comms.
 * Delivery is gated on delivery.read, matching the route guard in routeTable.
 * The delivery badge derives from the LIVE listMessageHistory op (docket
 * 2026-07-18 §2.1b) — the fictional DELIVERY_ROWS constant is deleted.
 *
 * Booking comms (`apptcomms`) was RESTORED here on 2026-09-02 (operator): its
 * registration went out with `ApptComms.tsx` between 08-25 and 08-31 with no
 * record while the lane it drives stayed live. It carries no count — the
 * template set is a fixed catalogue, not a library the operator grows.
 *
 * DELIVERY BADGE, JAI-1457. Three things were wrong at once and they compounded.
 * The badge read `(r.data || []).length` — the length of the PAGE — so it
 * reported the page size (200) instead of the count the response already
 * carries as `total`; it asked for 200 full delivery rows to compute one
 * integer; and the effect reset the cached value to `0` before every fetch, so
 * the number blinked to zero on every navigation into a Messaging screen and
 * only after a slow load showed the wrong figure. It now reads `total` through
 * a one-row request (`fetchDeliveryTotal`), caches PER TENANT, and seeds from
 * that cache instead of resetting — so the second visit is correct on the first
 * frame and a refetch never takes the badge back to zero.
 *
 * The cross-tenant leak the 2026-07-26 docket closed stays closed, and by
 * construction rather than by a purge: the cache is keyed by tenant slug, so
 * switching workspace reads a different key (absent -> 0) and refetches. It can
 * no longer show tenant A's count to tenant B, and it no longer discards A's
 * count when A is still the tenant.
 *
 * 2026-09-04 — the Messages / Workflows / Message delivery tabs are hidden from
 * the rail (operator). The switch and the landing-route helper live in
 * `messaging-visibility.ts` — see that file for why and how to un-hide.
 *
 * LOCAL PATCH (platform side, 2026-09-08 full resync r2): this is CD's 2026-09-07 base (the HIDDEN_TABS filter and the
 * "Booking Settings" label are theirs) with the JAI-1457 delivery-total delta re-applied
 * on top, and the apptcomms tab gated on `campaigns.read` — the capability the backend
 * enforces on the appointment-comms lineage (`listStudioMessages`); `appointments.manage`
 * is declared by no catalog, so the tab and the rail landing it feeds were unreachable.
 *
 * ABSORBED 2026-09-09 (CD, item 2 of the 0854 docket): taken verbatim from
 * origin/main 6cd61bf1f — the per-tenant `Map` cache, the in-flight dedupe, the
 * render-time derivation and `prefetchMessagingCounts` are all as sent. ONE
 * clause is deliberately NOT absorbed: the apptcomms gate. The paragraph above
 * describes `campaigns.read`, which was correct against CD's 2026-09-07 base,
 * but this tree moved that gate to `settings.read` on 2026-09-08 by operator
 * decision (Booking Settings is System Administrator only — `settings.read` to
 * see the tab, `settings.write` to edit, which the screen enforces itself).
 * Restoring `campaigns.read` here would revert an operator call silently, and
 * the gate is not part of JAI-1457. Flagged back to the platform line in
 * `cc-outbox/2026-09-09-item2-delivery-cache-absorbed.md`; if the backend truly
 * enforces `campaigns.read` on this lane, that is a capability question for the
 * operator, not a merge artifact.
 */
import { useEffect, useState } from "react";
import type { NavApi } from "../../shell/router";
import type { PrimaryNavTab } from "../../shell/chrome";
import { can, useTenant } from "../../shell/access";
import { useMessaging } from "./useMessaging";
import { useFlows } from "./useFlows";
import { fetchDeliveryTotal } from "./message-history-api";
import { t as i18nT } from "../../lib/i18n";
import { HIDDEN_TABS } from "./messaging-visibility";

export interface MessagingCounts { messages: number; flows: number; delivery: number; }

/* API channel vocabulary used by the automatic-message data adapter. Push and
   webhook are delivery transports, not customer-facing message channels. This
   tuple remains here for the adapter after the catalogue route was retired. */

export const AUTO_MESSAGE_CHANNELS = ["email", "sms", "whatsapp"] as const;
export type AutoMessageChannel = (typeof AUTO_MESSAGE_CHANNELS)[number];

export function isAutoMessageChannel(value: unknown): value is AutoMessageChannel {
  return typeof value === "string" && (AUTO_MESSAGE_CHANNELS as readonly string[]).includes(value);
}

/** Delivery TOTAL per tenant slug — survives unmount, so the badge never re-blanks. */
const _deliveryTotals = new Map<string, number>();
/** One in-flight request per tenant, so four Messaging screens don't fire four. */
const _deliveryInFlight = new Map<string, Promise<number>>();

/** The badge value for a tenant with no answer of its own yet. */
function cachedDeliveryTotal(slug: string | null): number {
  return slug ? _deliveryTotals.get(slug) ?? 0 : 0;
}

function loadDeliveryTotal(slug: string | null): Promise<number> | null {
  if (!slug || !can("delivery.read")) return null;
  const existing = _deliveryInFlight.get(slug);
  if (existing) return existing;
  const pending = fetchDeliveryTotal()
    .then((total) => { _deliveryTotals.set(slug, total); _deliveryInFlight.delete(slug); return total; })
    .catch((error) => { _deliveryInFlight.delete(slug); throw error; });
  _deliveryInFlight.set(slug, pending);
  return pending;
}

/**
 * Warm the delivery badge before a Messaging screen mounts.
 *
 * ⚠️ EXPORTED AND NOT YET CALLED, and that is a statement about the shell rather
 * than an oversight: nothing in `shell/chrome-rail.tsx` or `shell/router.tsx`
 * offers a "section entered" seam to hang this on — a rail click goes straight
 * to `nav.go` and the router keeps no subscriber list. Wiring one means editing
 * shell files this change deliberately does not touch. Until then the badge is
 * correct on the first frame of every visit after the first, from the cache
 * above; on the very first visit it fills as soon as the one-row read returns.
 */
export function prefetchMessagingCounts(slug: string | null): void {
  const pending = loadDeliveryTotal(slug);
  // The value lands in the cache; a rejection is the Delivery screen's to report.
  if (pending) pending.catch(() => { /* no badge, no crash */ });
}

/** Live tab counts for the Messaging rail (composes the feature data hooks). */
export function useMessagingCounts(): MessagingCounts {
  const slug = useTenant()?.slug ?? null;
  const messages = useMessaging().all().length;
  const flows = useFlows().all().length;
  // THE STATE REMEMBERS WHICH TENANT IT IS ABOUT, and the displayed value is
  // DERIVED FROM THAT DURING RENDER rather than corrected afterwards in the
  // effect. Both halves are load-bearing:
  //
  //   · A bare number could not tell a stale value from a current one, so an
  //     in-place workspace switch left the PREVIOUS tenant's count on screen
  //     for the length of the new fetch — the cross-tenant display the
  //     2026-07-26 docket closed. Pairing the number with its slug makes a
  //     stale value unusable rather than merely unlucky.
  //   · Resetting inside the effect would not be enough either. `useEffect` is
  //     passive: React renders and PAINTS the old state once before it runs, so
  //     an effect-side reset still shows the outgoing tenant's number for a
  //     frame. Deriving it in the render body means the first commit after the
  //     switch already carries the right value.
  //
  // The no-blink optimisation is unchanged and is the `?? 0` fallback's other
  // side: a tenant whose count is cached shows it on the first frame; a tenant
  // with none shows zero — the only honest value until its own total arrives.
  const [fetched, setFetched] = useState<{ slug: string | null; total: number }>(
    () => ({ slug, total: cachedDeliveryTotal(slug) }),
  );
  const delivery = fetched.slug === slug ? fetched.total : cachedDeliveryTotal(slug);
  useEffect(() => {
    let live = true;
    const pending = loadDeliveryTotal(slug);
    // No tenant, or no `delivery.read`: nothing will arrive, and the derived
    // value above has already said so.
    if (!pending) return;
    pending
      .then((total) => { if (live) setFetched({ slug, total }); })
      .catch(() => { /* keep whatever we had — the Delivery screen owns the error state */ });
    return () => { live = false; };
  }, [slug]);
  return { messages, flows, delivery };
}

export function messagingTabs(nav: NavApi, _active: string, counts: MessagingCounts) {
  const tabs: PrimaryNavTab[] = [
    { id: "messages", label: i18nT("messaging.tabs.messages", "Messages"), count: counts.messages, onClick: () => nav.go("messaging") },
  ];
  if (can("flows.read")) {
    tabs.push({ id: "flows", label: i18nT("messaging.tabs.messageFlows", "Message flows"), count: counts.flows, onClick: () => nav.go("messaging.flows") });
  }
  if (can("delivery.read")) {
    tabs.push({ id: "delivery", label: i18nT("messaging.tabs.messageDelivery", "Message delivery"), count: counts.delivery, onClick: () => nav.go("messaging.delivery") });
  }
  // Booking Settings (2026-09-08, Nick): System Administrator only — settings.read
  // AND messaging.templates.read to see it (CC 0357: the screen reads the templates,
  // which are the Message builder's text); the screen gates edits itself. NOT
  // campaigns.read; see the ABSORBED note in the docblock.
  if (can("settings.read") && can("messaging.templates.read")) {
    tabs.push({ id: "apptcomms", label: i18nT("messaging.tabs.bookingComms", "Booking Settings"), onClick: () => nav.go("messaging.appointments") });
  }
  // Message builder (2026-10-02, operator) — shared email layouts and the email +
  // SMS templates per message type. Its own capability family, held only by System
  // Administrator by default; the screen gates edits on messaging.templates.write.
  if (can("messaging.templates.read")) {
    tabs.push({ id: "builder", label: i18nT("messaging.tabs.messageBuilder", "Message builder"), onClick: () => nav.go("messaging.builder") });
  }
  // Sender settings — its own capability family (`settings.read` to see it,
  // `settings.write` to edit, which the screen enforces itself). Gating it on
  // `campaigns.read` would show an operator a tab whose every field is blocked.
  if (can("settings.read")) {
    tabs.push({ id: "settings", label: i18nT("messaging.tabs.settings", "Settings"), onClick: () => nav.go("messaging.settings") });
  }
  return tabs.filter((tab) => !HIDDEN_TABS.has(tab.id));
}
